/**
 * DCS Content Plugin for Vite
 *
 * Reads `.dcs/content.yaml` at build time and injects content
 * as `__DCS_CONTENT__` global variable for use by useTextContent.
 *
 * @example
 * ```typescript
 * // vite.config.ts
 * import { dcsContentPlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   plugins: [
 *     dcsContentPlugin({ debug: true })
 *   ]
 * })
 * ```
 *
 * For VitePress:
 * ```typescript
 * // .vitepress/config.ts
 * import { defineConfig } from 'vitepress'
 * import { dcsContentPlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   vite: {
 *     plugins: [
 *       dcsContentPlugin()
 *     ]
 *   }
 * })
 * ```
 */

import fs from 'node:fs'
import path from 'node:path'
import yaml from 'js-yaml'
import type { Plugin, ResolvedConfig, HtmlTagDescriptor } from 'vite'
import type { DcsContentFile } from '../types/content'
import { readDcsYamlFresh, buildGlobalAssignScript } from './serveDefineInject'
import { dcsMotionTokensStyleTag } from './motionTokens'
import { dcsAgentWidgetLoaderTag } from './agentWidget'

export interface DcsContentPluginOptions {
  /** Path to content.yaml relative to project root (default: '.dcs/content.yaml') */
  contentPath?: string
  /** Enable debug logging */
  debug?: boolean
}

/**
 * Packages that MUST be bundled into the SSR build (noExternal) rather than
 * externalized to `node_modules`.
 *
 * WHY: this plugin injects the site's content as the `__DCS_CONTENT__` Vite
 * `define`. A `define` is a compile-time text substitution that Vite applies
 * ONLY to source that passes through its transform pipeline. When a package is
 * SSR-externalized (loaded by Node's native resolver at server-render time), the
 * substitution never happens — `__DCS_CONTENT__` stays an undefined global on
 * the server. The content composables (`useReviewContent`, `useTextContent`)
 * then fall back to their empty/placeholder branch during SSR while the client
 * (where the define DID apply) renders the real content — producing the
 * "Hydration completed but contains mismatches" bug and, worse, baking
 * fabricated fallback content into the crawler-visible SSR HTML.
 *
 * Forcing the cms package `noExternal` bundles it into the SSR graph so the
 * define reaches the composables server-side, keeping SSR and client in lockstep
 * and putting the REAL content in the pre-rendered HTML. This is applied by the
 * plugin itself (merged into the user's Vite config) so every VitePress / vite
 * consumer inherits the fix on a cms bump — no per-site config knob to forget.
 */
const DCS_SSR_NO_EXTERNAL = ['@duffcloudservices/cms']

/**
 * Vite plugin that injects .dcs/content.yaml at build time.
 *
 * @param options - Plugin configuration
 * @returns Vite plugin
 */
export function dcsContentPlugin(options: DcsContentPluginOptions = {}): Plugin {
  const { contentPath = '.dcs/content.yaml', debug = false } = options

  let resolvedConfig: ResolvedConfig
  // Captured during configResolved — gates the serve-only inline HTML inject.
  let isDev = false

  return {
    name: 'dcs-content',

    configResolved(config) {
      resolvedConfig = config
      isDev = config.command === 'serve'
    },

    config(config, env) {
      // ── Dev / preview (serve) ──────────────────────────────────────────────
      // Don't bake content into the bundle. Redirect the `__DCS_CONTENT__`
      // define to a browser global that `transformIndexHtml` (below) refreshes
      // from disk on every HTML request — so a plain iframe reload reflects the
      // latest on-disk content with NO server.restart() and NO module-graph
      // surgery. The consumer is untouched: it still reads the bare
      // `__DCS_CONTENT__` identifier, which this define rewrites to
      // `globalThis.__DCS_CONTENT__`.
      if (env.command === 'serve') {
        return {
          define: {
            __DCS_CONTENT__: 'globalThis.__DCS_CONTENT__',
          },
          ssr: { noExternal: DCS_SSR_NO_EXTERNAL },
        }
      }

      // ── Production build ───────────────────────────────────────────────────
      // Unchanged: read content.yaml now and bake it into the bundle as a
      // JSON literal so the SSG / SPA output inlines content exactly as before.
      const projectRoot = config.root || process.cwd()

      // Try to find content.yaml in multiple locations
      // VitePress projects have the docs folder as root, so check parent too
      const possiblePaths = [
        path.resolve(projectRoot, contentPath),
        path.resolve(projectRoot, '..', contentPath),
        path.resolve(process.cwd(), contentPath),
      ]

      let foundPath: string | undefined
      for (const testPath of possiblePaths) {
        if (fs.existsSync(testPath)) {
          foundPath = testPath
          break
        }
      }

      if (!foundPath) {
        if (debug) {
          console.log('[dcs-content] No content.yaml found at:')
          possiblePaths.forEach((p) => console.log(`  - ${p}`))
          console.log('[dcs-content] Using defaults only')
        }
        return {
          define: {
            __DCS_CONTENT__: 'undefined',
          },
          ssr: { noExternal: DCS_SSR_NO_EXTERNAL },
        }
      }

      try {
        const fileContent = fs.readFileSync(foundPath, 'utf8')
        const content = yaml.load(fileContent) as DcsContentFile

        if (debug) {
          console.log(`[dcs-content] Loaded ${foundPath}`)
          console.log(`[dcs-content] Version: ${content.version}`)
          console.log(`[dcs-content] Pages: ${Object.keys(content.pages ?? {}).join(', ') || '(none)'}`)
          console.log(`[dcs-content] Global keys: ${Object.keys(content.global ?? {}).length}`)
        }

        return {
          define: {
            __DCS_CONTENT__: JSON.stringify(content),
          },
          ssr: { noExternal: DCS_SSR_NO_EXTERNAL },
        }
      } catch (error) {
        console.warn('[dcs-content] Failed to parse content.yaml:', error)
        return {
          define: {
            __DCS_CONTENT__: 'undefined',
          },
          ssr: { noExternal: DCS_SSR_NO_EXTERNAL },
        }
      }
    },

    /**
     * Inject into `<head>` on every HTML request. Two concerns:
     *
     * 1. The constant DCS motion-token `:root` block (`DCS_MOTION_TOKENS_CSS`)
     *    — the fleet-wide seam that gives every DCS site the shared `--motion-*`
     *    vocabulary. It carries no per-site data, so (unlike the content inject)
     *    it is NOT gated on `isDev`: it runs in both `serve` and `build`.
     *    It is injected `head` (append), NOT `head-prepend` — C-414. Appending
     *    still lands it ABOVE the bundled site CSS (Vite injects the build's
     *    `<link rel=stylesheet>` AFTER this `order: 'pre'` hook's tags), so a
     *    site's own `:root` (theme `tailwind.css` / `style.css`) can still
     *    override a token — while leaving the site's `<meta charset>` first in
     *    `<head>` instead of 858 bytes deep. See `motionTokens.ts`.
     *
     * 2. Dev-only: the FRESH on-disk `content.yaml` as an inline
     *    `globalThis.__DCS_CONTENT__ = {…}` classic script. Runs on every dev
     *    HTML request, so the portal's `?_hc` hard reload re-reads the YAML with
     *    no restart. A classic `<script>` in `<head>` executes before the
     *    deferred module app entry, so the global is set before the consumer's
     *    `useTextContent` setup reads it. Gated on `isDev` so it never runs in
     *    `vite`/`vitepress build` (the build path keeps the baked `define`).
     */
    transformIndexHtml: {
      order: 'pre',
      handler(html) {
        // (1) Always-on, per the note above: the constant motion-token block.
        const tags: HtmlTagDescriptor[] = [dcsMotionTokensStyleTag()]

        // (1b) Build-only: the AI-assistant widget loader. Rides this same
        //      fleet-wide seam (a cms bump ships it everywhere with no per-site
        //      edits) but is NOT injected into `vite dev` — its idle config
        //      fetch would hit the prod API from a non-whitelisted localhost
        //      origin and log an uncatchable CORS error for every site dev. The
        //      loader is fail-silent and returns null for non-DCS builds (no
        //      resolvable site slug), so nothing is injected there.
        if (!isDev) {
          const projectRoot = resolvedConfig?.root || process.cwd()
          const agentTag = dcsAgentWidgetLoaderTag({
            projectRoot,
            apiBaseUrl: process.env.VITE_API_BASE_URL,
          })
          if (agentTag) tags.push(agentTag)
        }

        // (2) Dev-only fresh content inject.
        if (isDev) {
          const projectRoot = resolvedConfig?.root || process.cwd()
          const content = readDcsYamlFresh<DcsContentFile>(
            projectRoot,
            contentPath,
            'dcs-content',
            debug
          )
          // No file (or unparseable) ⇒ leave the global unset; the consumer's
          // `typeof globalThis.__DCS_CONTENT__` check falls back to defaults.
          if (content !== undefined) {
            if (debug) {
              console.log('[dcs-content] serve: injecting fresh __DCS_CONTENT__ into <head>')
            }
            tags.push({
              tag: 'script',
              children: buildGlobalAssignScript('__DCS_CONTENT__', content),
              injectTo: 'head-prepend',
            })
          }
        }

        return { html, tags }
      },
    },

    // Watch for changes in development.
    //
    // Downgraded from `server.restart()` to a debounced client `full-reload`:
    // freshness already comes from `transformIndexHtml` re-reading the YAML on
    // every HTML request, so all we need on change is to nudge the browser to
    // reload. A `server.restart()` would rebuild VitePress's dev MiniSearch
    // index and can throw "server restart failed" on a duplicate heading id —
    // exactly the fragility this fix removes. A `full-reload` does neither.
    configureServer(server) {
      const projectRoot = resolvedConfig?.root || process.cwd()

      const watchPaths = [
        path.resolve(projectRoot, contentPath),
        path.resolve(projectRoot, '..', contentPath),
      ].map((p) => path.normalize(p))

      watchPaths.forEach((watchPath) => {
        if (fs.existsSync(watchPath)) {
          server.watcher.add(watchPath)
        }
      })

      let reloadTimer: ReturnType<typeof setTimeout> | undefined
      server.watcher.on('change', (changedPath) => {
        if (!watchPaths.includes(path.normalize(changedPath))) return
        if (reloadTimer) clearTimeout(reloadTimer)
        reloadTimer = setTimeout(() => {
          if (debug) {
            console.log('[dcs-content] content.yaml changed, sending full-reload')
          }
          server.ws.send({ type: 'full-reload' })
        }, 100)
      })
    },
  }
}
